import { Text } from "@ui-kitten/components";
import { FeeDetails, SignupChargeDetails } from "features/types";
import React from "react";
import { View } from "react-native";
import { formatCurrency } from "utils/appUtils";
import { LINE, PAPER } from "../helpers";

// One fee structure can be quoted once per plan instance (multi-buy, one
// per child); group by name and sum, like the legacy OrderSummary.
const groupFees = (fees: FeeDetails[] | null) => {
  const grouped = new Map<string, { amount: number; description?: string }>();
  (fees ?? []).forEach((fee) => {
    const entry = grouped.get(fee.structureDetails.name) ?? {
      amount: 0,
      description: fee.structureDetails.description,
    };
    entry.amount += Number(fee.feeAmount);
    grouped.set(fee.structureDetails.name, entry);
  });
  return [...grouped.entries()];
};

const Row = ({
  label,
  value,
  emphasized,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
}) => (
  <View className="flex-row justify-between items-center py-1">
    <Text category={emphasized ? "s2" : "p2"}>{label}</Text>
    <Text category={emphasized ? "s2" : "p2"}>{value}</Text>
  </View>
);

interface Props {
  quote: SignupChargeDetails;
  // Fee-inclusive totals from useSignupCharge.
  totalCharge: number;
  totalRecurringCharge: number;
  monthly: boolean;
  // planPackage.allInclusivePrice: the plan advertises one all-in price,
  // so fees are never itemized to the shopper (issue #1414, same rule as
  // the legacy OrderSummary).
  allInclusivePrice: boolean;
}

// Itemized payment breakdown from the backend signup-charge quote:
// subtotal, each named fee from quote.signupFees, and the fee-inclusive
// total actually charged today. Renders nothing for all-inclusive-price
// plans and when the quote carries no fees — the single amount in the
// summary card already tells the story.
const PaymentBreakdownV3 = ({
  quote,
  totalCharge,
  totalRecurringCharge,
  monthly,
  allInclusivePrice,
}: Props) => {
  const feeRows = groupFees(quote.signupFees);
  if (allInclusivePrice || feeRows.length === 0) return null;

  return (
    <View
      testID="v3-payment-breakdown"
      className="border rounded-2xl p-4 mb-4"
      style={{ borderColor: LINE, backgroundColor: PAPER }}
    >
      <Text category="s1" style={{ marginBottom: 4 }}>
        Payment breakdown
      </Text>
      <Row
        label="Subtotal"
        value={`${formatCurrency(Number(quote.signupCharge))}`}
      />
      {feeRows.map(([name, { amount, description }]) => (
        <View key={name}>
          <Row label={name} value={`${formatCurrency(amount)}`} />
          {description ? (
            <Text category="c1" appearance="hint">
              {description}
            </Text>
          ) : null}
        </View>
      ))}
      <View
        className="mt-1 pt-1"
        style={{ borderTopWidth: 1, borderTopColor: LINE }}
      >
        <Row
          label="Total due today"
          value={`${formatCurrency(totalCharge)}`}
          emphasized
        />
      </View>
      {monthly ? (
        <Text category="c1" appearance="hint">
          Then {formatCurrency(totalRecurringCharge)}/mo, fees included.
        </Text>
      ) : null}
    </View>
  );
};

export default PaymentBreakdownV3;
